/**
 * Route Preloading System
 * 
 * Intelligently preloads route chunks to improve navigation speed.
 * Uses hover detection and idle time to load chunks before they're needed.
 * 
 * Features:
 * - Hover-based preloading (load on mouse enter)
 * - Idle-time preloading (load during browser idle)
 * - Priority-based loading (critical routes first)
 * - Deduplication (don't load same chunk twice)
 */

interface PreloadConfig {
  route: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  preloadOn: 'hover' | 'idle' | 'both';
}

// Routes to preload with their priorities
const PRELOAD_ROUTES: PreloadConfig[] = [
  // Critical routes - always preload on idle
  { route: '/admin', priority: 'critical', preloadOn: 'both' },
  { route: '/leads', priority: 'critical', preloadOn: 'both' },

  // High priority - preload on hover
  { route: '/properties', priority: 'high', preloadOn: 'hover' },
  { route: '/agendamentos', priority: 'high', preloadOn: 'hover' },
  { route: '/leads/kanban', priority: 'high', preloadOn: 'hover' },

  // Medium priority - preload on hover only
  { route: 'customization', priority: 'medium', preloadOn: 'hover' },
  { route: '/marketing', priority: 'medium', preloadOn: 'hover' },
  { route: '/settings', priority: 'medium', preloadOn: 'hover' },
];

// Track which routes have been preloaded
const preloadedRoutes = new Set<string>();

// Track preload promises to avoid duplicates
const preloadPromises = new Map<string, Promise<any>>();

/**
 * Preload a route's chunk
 * Returns immediately if already preloaded
 */
export const preloadRoute = async (route: string): Promise<void> => {
  // Already preloaded
  if (preloadedRoutes.has(route)) {
    return;
  }

  // Already preloading
  if (preloadPromises.has(route)) {
    return preloadPromises.get(route);
  }

  // Start preloading
  const promise = (async () => {
    try {
      // Map routes to their lazy components
      const routeMap: Record<string, () => Promise<any>> = {
        '/admin': () => import('@/pages/admin/Dashboard'),
        '/leads': () => import('@/pages/admin/Leads'),
        '/properties': () => import('@/pages/admin/Properties'),
        '/agendamentos': () => import('@/pages/admin/Agendamentos'),
        '/leads/kanban': () => import('@/pages/admin/LeadsKanban'),
        'customization': () => import('@/pages/admin/Customization'),
        '/marketing': () => import('@/pages/admin/Marketing'),
        '/settings': () => import('@/pages/admin/Settings'),
      };

      const loader = routeMap[route];
      if (loader) {
        await loader();
        preloadedRoutes.add(route);
        console.log(`[Preload] ✅ Loaded: ${route}`);
      }
    } catch (error) {
      console.error(`[Preload] ❌ Failed to load ${route}:`, error);
    } finally {
      preloadPromises.delete(route);
    }
  })();

  preloadPromises.set(route, promise);
  return promise;
};

/**
 * Preload routes based on priority during idle time
 * Uses requestIdleCallback for non-blocking loading
 */
export const preloadIdleRoutes = (): void => {
  // Check if browser supports requestIdleCallback
  if (typeof requestIdleCallback === 'undefined') {
    // Fallback to setTimeout
    setTimeout(() => preloadIdleRoutesSync(), 2000);
    return;
  }

  requestIdleCallback(
    () => {
      preloadIdleRoutesSync();
    },
    { timeout: 5000 } // Max 5s wait
  );
};

/**
 * Synchronous version of idle preloading
 */
const preloadIdleRoutesSync = (): void => {
  // Get routes that should be preloaded on idle
  const idleRoutes = PRELOAD_ROUTES
    .filter(config => config.preloadOn === 'idle' || config.preloadOn === 'both')
    .sort((a, b) => {
      // Sort by priority
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

  // Preload routes sequentially with delays
  let delay = 0;
  idleRoutes.forEach(config => {
    setTimeout(() => {
      preloadRoute(config.route);
    }, delay);
    delay += 500; // 500ms between each preload
  });
};

/**
 * Hook for preloading on hover
 * Returns handlers to attach to Link components
 */
export const useRoutePreload = (route: string) => {
  const config = PRELOAD_ROUTES.find(r => r.route === route);

  // Only preload if configured for hover
  const shouldPreload = config &&
    (config.preloadOn === 'hover' || config.preloadOn === 'both');

  const handleMouseEnter = () => {
    if (shouldPreload) {
      preloadRoute(route);
    }
  };

  const handleTouchStart = () => {
    if (shouldPreload) {
      preloadRoute(route);
    }
  };

  return {
    onMouseEnter: handleMouseEnter,
    onTouchStart: handleTouchStart,
  };
};

/**
 * Preload multiple routes at once
 * Useful for preloading related routes
 */
export const preloadRoutes = async (routes: string[]): Promise<void> => {
  await Promise.all(routes.map(route => preloadRoute(route)));
};

/**
 * Get preload statistics
 * Useful for debugging and monitoring
 */
export const getPreloadStats = () => {
  return {
    preloadedCount: preloadedRoutes.size,
    preloadedRoutes: Array.from(preloadedRoutes),
    pendingCount: preloadPromises.size,
    pendingRoutes: Array.from(preloadPromises.keys()),
  };
};

/**
 * Clear preload cache
 * Useful for testing or memory management
 */
export const clearPreloadCache = () => {
  preloadedRoutes.clear();
  preloadPromises.clear();
};
